import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Building2, CheckCircle, Clock, FileText, Plus, Users, LogOut, User, Shield, Upload, Leaf, Settings, ChevronDown } from 'lucide-react';
import BatchTable from './BatchTable';
import StatCard from './StatCard';
import SearchBar from './SearchBar';
import SubmitModal from './modals/SubmitModal';
import BatchDetailsModal from './modals/BatchDetailsModal';
import ClientManagerModal from './modals/ClientManagerModal';
import CompanyManagerModal from './modals/CompanyManagerModal';
import CompanyReportModal from './modals/CompanyReportModal';
import SampleTotalsModal from './modals/SampleTotalsModal';
import LabResultsUploadModal from './modals/LabResultsUploadModal';
import CropProfilesModal from './modals/CropProfilesModal';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE, authFetch } from '../constants';

function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showClientManager, setShowClientManager] = useState(false);
  const [showCompanyManager, setShowCompanyManager] = useState(false);
  const [showCompanyReport, setShowCompanyReport] = useState(false);
  const [showSampleTotals, setShowSampleTotals] = useState(false);
  const [showLabResultsUpload, setShowLabResultsUpload] = useState(false);
  const [showCropProfiles, setShowCropProfiles] = useState(false);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsRef = useRef(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    batchId: '',
    company: '',
    grower: '',
    status: '',
    submittedBy: '',
    dateFrom: '',
    dateTo: '',
    minSamples: ''
  });

  useEffect(() => {
    fetchBatches();
    fetchStats();
  }, []);

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBatches = async () => {
    try {
      const res = await authFetch(`${API_BASE}/batches/`);
      const data = await res.json();
      setBatches(data);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await authFetch(`${API_BASE}/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const downloadLabCSV = async (batchId) => {
    try {
      // Get batch info to retrieve the already-computed filename from csv_path
      const batchRes = await authFetch(`${API_BASE}/batches/${batchId}`);
      const batchData = await batchRes.json();

      // Use the filename the backend stored when CSV was generated (e.g. 8_0_25-00035.csv)
      // This avoids re-implementing routing logic in the frontend
      let filename;
      if (batchData.batch?.csv_path) {
        filename = batchData.batch.csv_path.split('/').pop();
      } else {
        filename = `${batchId}_lab_submission.csv`; // fallback (should not occur)
      }

      const res = await authFetch(`${API_BASE}/batches/${batchId}/download-csv`);
      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      fetchBatches();
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download CSV');
    }
  };

  const generateLabCSV = async (batchId) => {
    try {
      await authFetch(`${API_BASE}/batches/${batchId}/generate-csv`, { method: 'POST' });
      alert('Batch marked as Ready to Ship! Admin will be able to download the CSV.');
      fetchBatches();
    } catch (err) {
      alert('Failed to mark batch as ready to ship');
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm(`Delete batch ${batchId}? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Batch deleted successfully');
        fetchBatches();
        fetchStats();
      } else {
        alert('Failed to delete batch');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSelectBatch = (batchId) => {
    setSelectedBatchId(batchId);
    setShowBatchDetails(true);
  };

  const handleCloseBatchDetails = () => {
    setShowBatchDetails(false);
    setSelectedBatchId(null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      batchId: '',
      company: '',
      grower: '',
      status: '',
      submittedBy: '',
      dateFrom: '',
      dateTo: '',
      minSamples: ''
    });
  };

  // Filter batches based on search criteria
  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      // Filter by Batch ID
      if (filters.batchId && !(batch.batch_id || '').toLowerCase().includes(filters.batchId.toLowerCase()) && 
          !(batch.full_batch_id || '').toLowerCase().includes(filters.batchId.toLowerCase())) {
        return false;
      }

      // Filter by Company
      if (filters.company && !(batch.company_name || '').toLowerCase().includes(filters.company.toLowerCase())) {
        return false;
      }

      // Filter by Grower
      if (filters.grower && !(batch.grower_names || '').toLowerCase().includes(filters.grower.toLowerCase())) {
        return false;
      }

      // Filter by Status
      if (filters.status && batch.status !== filters.status) {
        return false;
      }

      // Filter by Submitted By
      if (filters.submittedBy && !(batch.submitted_by || '').toLowerCase().includes(filters.submittedBy.toLowerCase())) {
        return false;
      }

      // Filter by Date Range (From)
      if (filters.dateFrom) {
        const batchDate = new Date(batch.submission_date);
        const fromDate = new Date(filters.dateFrom);
        if (batchDate < fromDate) {
          return false;
        }
      }

      // Filter by Date Range (To)
      if (filters.dateTo) {
        const batchDate = new Date(batch.submission_date);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Include the entire end date
        if (batchDate > toDate) {
          return false;
        }
      }

      // Filter by Minimum Samples
      if (filters.minSamples && batch.sample_count < parseInt(filters.minSamples)) {
        return false;
      }

      return true;
    });
  }, [batches, filters]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <img src="/KASlogoSSP.png" alt="Kinsey Ag Services Soil Submission Portal" className="h-16 w-auto" />
            </div>
            
            {/* User Info & Actions */}
            <div className="flex items-center gap-2">
              {/* User Info Card */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <User size={16} className="text-gray-600 shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-gray-900 leading-tight">{user?.full_name || user?.username}</div>
                  <div className="text-xs text-gray-500">
                    {isAdmin() ? (
                      <span className="flex items-center gap-1">
                        <Shield size={10} className="text-blue-600" />
                        Administrator
                      </span>
                    ) : (
                      user?.company_name || 'User'
                    )}
                  </div>
                </div>
              </div>

              {/* Primary actions — always visible */}
              <button
                onClick={() => setShowClientManager(true)}
                className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition text-sm whitespace-nowrap"
              >
                <Users size={15} />
                Clients
              </button>

              <button
                onClick={() => setShowSubmitModal(true)}
                className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition text-sm whitespace-nowrap"
              >
                <Plus size={15} />
                Submit Samples
              </button>

              {/* Settings dropdown — admin only */}
              {isAdmin() && (
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => setShowSettingsMenu((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition text-sm whitespace-nowrap ${
                      showSettingsMenu
                        ? 'bg-gray-800 text-white border-gray-800'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Admin Settings"
                  >
                    <Settings size={15} />
                    <ChevronDown size={13} className={`transition-transform ${showSettingsMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showSettingsMenu && (
                    <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                      <button
                        onClick={() => { setShowCompanyManager(true); setShowSettingsMenu(false); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <Building2 size={15} className="text-purple-600" />
                        Company Manager
                      </button>
                      <button
                        onClick={() => { setShowLabResultsUpload(true); setShowSettingsMenu(false); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <Upload size={15} className="text-orange-600" />
                        Upload Lab Results
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => { setShowCropProfiles(true); setShowSettingsMenu(false); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <Leaf size={15} className="text-green-600" />
                        Crop Profiles
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Logout */}
              <button
                onClick={logout}
                className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition text-sm whitespace-nowrap"
                title="Sign Out"
              >
                <LogOut size={15} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {stats && (
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-1 gap-4 ${isAdmin() ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <StatCard icon={<FileText />} label="Total Batches" value={stats.total_batches} />
            <StatCard icon={<CheckCircle />} label="Completed" value={stats.completed_batches} />
            <StatCard 
              icon={<Clock />} 
              label="Total Samples" 
              value={stats.total_samples}
              onClick={() => setShowSampleTotals(true)}
            />
            {isAdmin() && (
              <StatCard 
                icon={<Building2 />} 
                label="Companies" 
                value={stats.total_companies}
                onClick={() => setShowCompanyReport(true)}
              />
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <SearchBar 
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          isAdmin={isAdmin()}
        />
        
        <BatchTable
          batches={filteredBatches}
          totalCount={batches.length}
          loading={loading}
          onSelectBatch={handleSelectBatch}
          onGenerateCsv={generateLabCSV}
          onDownloadCsv={downloadLabCSV}
          onRefresh={fetchBatches}
          isAdmin={isAdmin()}
        />
      </div>

      {showSubmitModal && (
        <SubmitModal
          onClose={() => setShowSubmitModal(false)}
          onSuccess={() => {
            fetchBatches();
            fetchStats();
            setShowSubmitModal(false);
          }}
        />
      )}

      {showClientManager && <ClientManagerModal onClose={() => setShowClientManager(false)} />}

      {showCompanyManager && <CompanyManagerModal onClose={() => setShowCompanyManager(false)} />}

      {showCompanyReport && <CompanyReportModal onClose={() => setShowCompanyReport(false)} />}

      {showSampleTotals && <SampleTotalsModal onClose={() => setShowSampleTotals(false)} />}

      {showLabResultsUpload && (
        <LabResultsUploadModal
          onClose={() => setShowLabResultsUpload(false)}
          onSuccess={() => {
            fetchBatches();
            fetchStats();
            setShowLabResultsUpload(false);
          }}
        />
      )}

      {showCropProfiles && (
        <CropProfilesModal onClose={() => setShowCropProfiles(false)} />
      )}

      {showBatchDetails && selectedBatchId && (
        <BatchDetailsModal
          batchId={selectedBatchId}
          onClose={handleCloseBatchDetails}
          onDelete={() => {
            deleteBatch(selectedBatchId);
            handleCloseBatchDetails();
          }}
          onGenerateCSV={() => generateLabCSV(selectedBatchId)}
          onDownloadCSV={() => downloadLabCSV(selectedBatchId)}
          isAdmin={isAdmin()}
        />
      )}
    </div>
  );
}

export default Dashboard;
